import React, { useState, useRef } from "react";

import { StyledBlock } from "../containers";
import { Label } from "../generic";

import { ISelectItems, StrNum } from "interfaces/intarfaces";
import { ArrowDown } from "components/PopupMenu/styles";
import {
  StyledPrefix,
  ContainerSelectedValue,
  ItemsMultiSelect,
  CloseButton,
  PopupSelect,
  ItemLi,
} from "./styles";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import useHover from "utils/hooks/useHover";
import { theme } from "styles/theme";
import { sortTop } from "utils/fetchMapInfo";
// sortItems,
import { useTranslation } from 'react-i18next';


export interface ISelectMulti extends React.HTMLAttributes<HTMLSelectElement> {
  items?: ISelectItems[];
  selected?: ISelectItems[];
  values?: string[];
  title?: string;
  placeholder?: string;
  changeSelected?: (x: StrNum, mode: boolean) => void;
  p?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
}

const MultiSelect: React.FC<ISelectMulti> = ({
  items = [],
  selected = [],
  title,
  placeholder,
  changeSelected,
  p,
  id,
  disabled,
  required = false,
  ...props
}) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  useOnClickOutside(ref, () => !isHovered && setShow(false));

  return (
    <StyledBlock p={p} id={id}>
      {title && (
        <Label p="0" bgColor="transparent" fontWeight="600">
          {title}
        </Label>
      )}
      <StyledBlock onClick={() => !disabled && setShow(!show)} ref={hoverRef}>
        <StyledPrefix>
          <ArrowDown size="2em" up={show} />
        </StyledPrefix>
        <ContainerSelectedValue error={required && selected.length === 0}>
          {selected.length === 0 ? (
            <ItemsMultiSelect
              bgColor="transparent"
              fontColor={theme.colors.textMuted}
            >
              {placeholder}
            </ItemsMultiSelect>
          ) : (
            selected.map((item) => (
              <ItemsMultiSelect key={item.value}>
                <>
                  {item.label}
                  <CloseButton
                    onClick={(e) => {
                      changeSelected && changeSelected(item.value, false);
                      e.stopPropagation();
                    }}
                  />
                </>
              </ItemsMultiSelect>
            ))
          )}
        </ContainerSelectedValue>
      </StyledBlock>
      {show && (
        <PopupSelect className={show ? "panel active" : "panel"} ref={ref}>
          {items &&
            items?.map((item) => (
              <ItemLi
                key={item.value}
                onClick={() => {
                  changeSelected && changeSelected(item.value, true);
                }}
              >
                {item.label}
              </ItemLi>
            ))}
        </PopupSelect>
      )}
    </StyledBlock>
  );
};

export default MultiSelect;

const separateItems = <T extends { value: StrNum }>(
  items: T[],
  keys: StrNum[],
  excludes?: boolean
): T[] => {
  if (excludes && keys.length === 0) {
    return [];
  } else if (!excludes && keys.length === 0) return items;
  return items.filter((item) =>
    excludes ? keys.includes(item.value) : !keys.includes(item.value)
  );
};

export const useMultiSelect = (
  items: ISelectItems[],
  initial?: StrNum[] | undefined,
  topValues?: string[]
) => {
  const [selectedItems, setSelectedItems] = useState<ISelectItems[]>(
    separateItems<ISelectItems>(items, initial || [], true)
  );

  const [lines, setLines] = useState<ISelectItems[]>(
    sortTop(separateItems<ISelectItems>(items, initial || []), topValues)
  );

  const { i18n } = useTranslation('home');
  const currLocale = i18n.language === 'gb' ? 'gb' : i18n.language;

  const sortItems = <T extends ISelectItems>(a: T, b: T): number => {
    if (!a.hasOwnProperty('label') || a.label === undefined) return 0;
    let aLabel = a.label.toLowerCase(),
      bLabel = b.label.toLowerCase();
    return aLabel.localeCompare(bLabel, currLocale);
  };



  const changeSelected = (value: StrNum, mode: boolean = true) => {
    if (mode) {
      setSelectedItems((prev) =>
        [...prev, ...lines.filter((item) => item.value === value)].sort(
          sortItems
        )
      );
      setLines((prev) =>
        sortTop(
          prev.filter((item) => item.value !== value).sort(sortItems),
          topValues ?? []
        )
      );
    } else {
      setLines((prev) =>
        sortTop(
          [
            ...prev,
            ...selectedItems.filter((item) => item.value === value),
          ].sort(sortItems),
          topValues ?? []
        )
      );
      setSelectedItems((prev) =>
        prev.filter((item) => item.value !== value).sort(sortItems)
      );
    }
  };

  return {
    selected: selectedItems,
    items: lines,
    changeSelected,
    values: selectedItems.map((item) => String(item.value)),
  };
};
